import { NextPageContext } from "next";
import nookies from "nookies";

const COOKIE_NAME = "bql_geo";

/**
 * Extracts the region from a geolocation cookie for US and Canadian users.
 *
 * @param context - Optional Next.js page context containing the request object used to access cookies
 * @returns The region code if the user is in the US or Canada and has a valid geolocation cookie; otherwise, null
 *
 * @remarks
 * This function parses a geolocation cookie to determine the user's region. It only returns
 * a region value for users located in the United States or Canada. For all other cases
 * (other countries, malformed cookies, or missing cookies), it returns null.
 *
 * @example
 * ```typescript
 * // Get region from server-side context
 * const region = getRegionFromGeoCookie(context);
 *
 * // Get region from client-side (no context)
 * const region = getRegionFromGeoCookie();
 * ```
 */
export function getRegionFromGeoCookie(context?: Pick<NextPageContext, "req"> | null | undefined): string | null {
  const cookie = nookies.get(context)[COOKIE_NAME];

  if (cookie) {
    try {
      const { country, region } = JSON.parse(cookie);
      if ((country === "US" || country === "CA") && region) {
        return region;
      }
    } catch (e) {
      return null;
    }
  }

  return null;
}

/**
 * Determines the locale used for location-specific content.
 *
 * A valid geolocation cookie takes precedence over a saved user locale.
 */
export function getPreferredUserLocale(userLocale?: string | null): string {
  return getRegionFromGeoCookie() ?? userLocale ?? "CA";
}
